import React, { useState, useEffect } from "react";
import moment from "moment";
import { useAppContext } from "../../AppContext";
import MPHomeLoginWithPhoneNumber from "./MPHomeLoginWithPhoneNumber";
import { useTranslation } from "react-i18next";

export default function MPHomeBookApp() {
  const {
    OpenDrawer,
    setOpenDrawer,
    ObjectData,
    setObjectData,
    AllApps,
    Loading,
    ChoosedDate,
    setChoosedDate,
    ChoosedApps,
    setChoosedApps,
    AllData,
    SelectedServices,
    setSelectedServices,
    User,
    setUser,
    OtherBarbers,
    setStep,
    step,
  } = useAppContext();
  const { t, i18n } = useTranslation();

  // const [step, setStep] = useState(1);
  const [SelectedBarber, setSelectedBarber] = useState(null);

  // Check if we should show the barber selection step
  const shouldShowBarberStep = OtherBarbers && OtherBarbers.length > 0;

  // Initialize user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem("User");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && !User) {
          setUser(parsedUser);
          // Auto-advance to appropriate step if already logged in
          if (shouldShowBarberStep) {
            setStep(4); // Go to barber selection
          } else {
            setStep(5); // Skip barber selection, go to services
          }
        }
      } catch (err) {
        console.error("Error parsing stored user:", err);
        localStorage.removeItem("User");
      }
    }
  }, []);

  // Reset to login step when user logs out
  useEffect(() => {
    if (!User) {
      setStep(1);
      setSelectedServices([]);
      setChoosedDate(null);
      setChoosedApps([]);
      setSelectedBarber(null);
    } else {
      // If user exists and we're on step 1-3, move to appropriate step
      if (step >= 1 && step <= 3) {
        if (shouldShowBarberStep) {
          setStep(4); // Go to barber selection
        } else {
          setStep(5); // Skip to services
        }
      }
    }
  }, [User]);

  const toggleService = (item) => {
    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.Service === item.service);
      if (exists) {
        return prev.filter((s) => s.Service !== item.service);
      } else {
        // Parse duration - handle formats like "30 min", "45 minutes", or just numbers
        let durationValue = 30; // default
        if (item.duration) {
          const parsed = parseInt(
            item.duration.toString().match(/\d+/)?.[0] || "30"
          );
          durationValue = parsed || 30;
        }

        return [
          ...prev,
          {
            Service: item.service,
            Price: item.price,
            Duration: durationValue,
          },
        ];
      }
    });
  };

  const days = Array.from({ length: 7 }, (_, i) => {
    const dayMoment = moment().add(i, "days");
    return {
      date: dayMoment.format("DD-MM-YYYY"),
      day: dayMoment.format("ddd"),
      fullDay: dayMoment.format("dddd"),
      dayNum: dayMoment.format("DD"),
    };
  });

  const totalPrice = SelectedServices.reduce(
    (sum, service) => sum + parseFloat(service.Price || 0),
    0
  );

  // Calculate total duration of selected services
  const totalDuration = SelectedServices.reduce(
    (sum, service) => sum + (service.Duration || 0),
    0
  );

  // Helper function to get valid starting slots
  const getAvailableStartingSlots = (appointments, requiredDuration) => {
    if (requiredDuration === 0) {
      return appointments
        .map((app, index) => ({ app, index }))
        .filter(({ app }) => app.isTaken === 0) // Only show completely free slots
        .map(({ index }) => index);
    }

    const validStartIndexes = [];
    const consumedIndexes = new Set();

    for (let i = 0; i < appointments.length; i++) {
      const app = appointments[i];

      // Skip if already consumed or taken (1) or occupied by another booking (4)
      if (consumedIndexes.has(i) || app.isTaken === 1 || app.isTaken === 4) {
        continue;
      }

      // Check if we can fit the duration starting from here
      let accumulatedTime = 0;
      let currentIndex = i;
      const slotsNeeded = [];

      while (
        currentIndex < appointments.length &&
        accumulatedTime < requiredDuration
      ) {
        const currentApp = appointments[currentIndex];

        // If taken (1) or occupied (4), can't continue
        if (currentApp.isTaken === 1 || currentApp.isTaken === 4) {
          break;
        }

        // Check if consecutive (if not first in chain)
        if (currentIndex > i) {
          const prevApp = appointments[currentIndex - 1];
          if (prevApp.timeEnd !== currentApp.timeStart) {
            // Not consecutive, break
            break;
          }
        }

        slotsNeeded.push(currentIndex);
        accumulatedTime += currentApp.timeLength;
        currentIndex++;
      }

      // If we accumulated enough time, this is a valid starting point
      if (accumulatedTime >= requiredDuration) {
        validStartIndexes.push(i);
        // Mark all slots except the starting one as consumed
        for (let j = 1; j < slotsNeeded.length; j++) {
          consumedIndexes.add(slotsNeeded[j]);
        }
      }
    }

    return validStartIndexes;
  };

  const handleNextToServices = () => {
    if (SelectedBarber || !shouldShowBarberStep) {
      setStep(5);
    }
  };

  const handleNextToDateTime = () => {
    if (SelectedServices.length > 0) {
      const today = moment().format("DD-MM-YYYY");
      setChoosedDate(today);
      const todaysAppointments = AllApps.filter((app) => app.date === today);
      setChoosedApps(todaysAppointments);
      setStep(6);
    }
  };

  const handleBackToBarber = () => {
    if (shouldShowBarberStep) {
      setStep(4);
    }
  };

  const handleBackToServices = () => {
    setStep(5);
  };

  const handleLoginSuccess = (userData) => {
    console.log("Login successful, saving user:", userData);

    if (userData) {
      setUser(userData);
      localStorage.setItem("User", JSON.stringify(userData));

      // Move to barber selection if available, otherwise to services
      if (shouldShowBarberStep) {
        setStep(4);
      } else {
        setStep(5);
      }
    }
  };

  if (!Loading) return <div style={{ padding: "20px" }}>Loading...</div>;

  // Calculate the actual step numbers for display
  const getDisplayStep = (actualStep) => {
    if (!shouldShowBarberStep) {
      // Without barber step: Login(1) -> Services(2) -> Book(3)
      if (actualStep <= 3) return 1; // Login
      if (actualStep === 5) return 2; // Services
      if (actualStep === 6) return 3; // Book
    } else {
      // With barber step: Login(1) -> Barber(2) -> Services(3) -> Book(4)
      if (actualStep <= 3) return 1; // Login
      if (actualStep === 4) return 2; // Barber
      if (actualStep === 5) return 3; // Services
      if (actualStep === 6) return 4; // Book
    }
    return 1;
  };

  const totalSteps = shouldShowBarberStep ? 4 : 3;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        paddingBottom: "160px",
      }}
    >
      {/* Overall Progress Indicator */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          padding: "20px 0 0 0",
        }}
      >
        {/* Auth Step */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: step <= 3 ? "#667eea" : "#10b981",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "12px",
            }}
          >
            {step > 3 ? "✓" : "1"}
          </div>
          <span
            style={{
              fontSize: "13px",
              fontWeight: step <= 3 ? "600" : "500",
              color: step <= 3 ? "#667eea" : "#10b981",
            }}
          >
            Login
          </span>
        </div>

        <div
          style={{
            width: "30px",
            height: "2px",
            backgroundColor: step > 3 ? "#10b981" : "#e5e7eb",
          }}
        />

        {/* Barber Step - Only show if there are other barbers */}
        {shouldShowBarberStep && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  backgroundColor:
                    step === 4 ? "#667eea" : step > 4 ? "#10b981" : "#e5e7eb",
                  color: step >= 4 ? "#fff" : "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "12px",
                }}
              >
                {step > 4 ? "✓" : "2"}
              </div>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: step === 4 ? "600" : "500",
                  color:
                    step === 4 ? "#667eea" : step > 4 ? "#10b981" : "#9ca3af",
                }}
              >
                Barber
              </span>
            </div>

            <div
              style={{
                width: "30px",
                height: "2px",
                backgroundColor: step > 4 ? "#10b981" : "#e5e7eb",
              }}
            />
          </>
        )}

        {/* Services Step */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor:
                step === 5 ? "#667eea" : step > 5 ? "#10b981" : "#e5e7eb",
              color: step >= 5 ? "#fff" : "#9ca3af",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "12px",
            }}
          >
            {step > 5 ? "✓" : shouldShowBarberStep ? "3" : "2"}
          </div>
          <span
            style={{
              fontSize: "13px",
              fontWeight: step === 5 ? "600" : "500",
              color: step === 5 ? "#667eea" : step > 5 ? "#10b981" : "#9ca3af",
            }}
          >
            Services
          </span>
        </div>

        <div
          style={{
            width: "30px",
            height: "2px",
            backgroundColor: step > 5 ? "#667eea" : "#e5e7eb",
          }}
        />

        {/* Date & Time Step */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: step === 6 ? "#667eea" : "#e5e7eb",
              color: step === 6 ? "#fff" : "#9ca3af",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "12px",
            }}
          >
            {shouldShowBarberStep ? "4" : "3"}
          </div>
          <span
            style={{
              fontSize: "13px",
              fontWeight: step === 6 ? "600" : "500",
              color: step === 6 ? "#667eea" : "#9ca3af",
            }}
          >
            Book
          </span>
        </div>
      </div>

      {/* User Info Banner - Show when logged in */}
      {User && step > 3 && (
        <div
          style={{
            margin: "0 16px",
            padding: "12px 16px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#fff",
          }}
        >
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700" }}>
              {User.name || "User"}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.9 }}>
              {User.phoneNumber}
            </div>
          </div>
          <div
            style={{
              fontSize: "24px",
            }}
          >
            ✓
          </div>
        </div>
      )}

      {/* STEPS 1-3: Authentication */}
      {step >= 1 && step <= 3 && (
        <div
          style={{
            maxWidth: "500px",
            margin: "0 auto",
            width: "100%",
            padding: "0 16px",
          }}
        >
          <MPHomeLoginWithPhoneNumber onLoginSuccess={handleLoginSuccess} />
        </div>
      )}

      {/* STEP 4: Choose Barber */}
      {step === 4 && shouldShowBarberStep && (
        <div style={{ padding: "0 16px" }}>
          <div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              Choose Your Barber
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              Select a barber for your appointment
            </p>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {OtherBarbers.map((barber, index) => {
                const isSelected =
                  SelectedBarber?.phoneNumber === barber.phoneNumber;
                return (
                  <div
                    key={index}
                    onClick={() => setSelectedBarber(barber)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "18px",
                      borderRadius: "14px",
                      backgroundColor: isSelected ? "#667eea" : "#fff",
                      color: isSelected ? "#fff" : "#1a1a1a",
                      border:
                        "2px solid " + (isSelected ? "#667eea" : "#e5e7eb"),
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: isSelected
                        ? "0 4px 16px rgba(102, 126, 234, 0.4)"
                        : "0 2px 6px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                      }}
                    >
                      {/* Barber Avatar */}
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          backgroundColor: isSelected ? "#fff" : "#667eea",
                          color: isSelected ? "#667eea" : "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "700",
                          fontSize: "18px",
                        }}
                      >
                        {barber.name?.charAt(0) || "B"}
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            marginBottom: "4px",
                          }}
                        >
                          {barber.name || "Barber"}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.85,
                          }}
                        >
                          {barber.specialty || "Professional Barber"}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "50%",
                        border: isSelected
                          ? "2px solid #fff"
                          : "2px solid #d1d5db",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isSelected ? "#fff" : "transparent",
                      }}
                    >
                      {isSelected && (
                        <div
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            backgroundColor: "#667eea",
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Empty State */}
          {!SelectedBarber && (
            <div
              style={{
                marginTop: "32px",
                padding: "40px 20px",
                textAlign: "center",
                backgroundColor: "#fef9e7",
                borderRadius: "16px",
                border: "2px solid #fde68a",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>💈</div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "6px",
                  fontSize: "16px",
                }}
              >
                {t("SelectABarber")}
              </div>
              <div style={{ fontSize: "14px", color: "#b45309" }}>
                {t("ChoosePreferredBarber")}
              </div>
            </div>
          )}

          {/* Next Button - Fixed at bottom */}
          <div
            style={{
              position: "fixed",
              bottom: "70px",
              left: 0,
              right: 0,
              padding: "16px",
              backgroundColor: "#fff",
              borderTop: "1px solid #e5e7eb",
              boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
              zIndex: 100,
            }}
          >
            <button
              onClick={handleNextToServices}
              disabled={!SelectedBarber}
              style={{
                width: "100%",
                maxWidth: "600px",
                margin: "0 auto",
                display: "block",
                padding: "18px",
                fontSize: "16px",
                fontWeight: "600",
                border: "none",
                borderRadius: "14px",
                backgroundColor: !SelectedBarber ? "#d1d5db" : "#667eea",
                color: "#fff",
                cursor: !SelectedBarber ? "not-allowed" : "pointer",
                boxShadow: !SelectedBarber
                  ? "none"
                  : "0 4px 16px rgba(102, 126, 234, 0.4)",
                transition: "all 0.3s ease",
                outline: "none",
              }}
            >
              {t("NextSelectServices")}
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: Select Services */}
      {step === 5 && (
        <div style={{ padding: "0 16px" }}>
          {/* Selected Barber Banner - Show if barber was selected */}
          {SelectedBarber && (
            <div
              style={{
                marginBottom: "20px",
                padding: "12px 16px",
                background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                borderRadius: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#fff",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    color: "#f59e0b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "16px",
                  }}
                >
                  {SelectedBarber.name?.charAt(0) || "B"}
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "700" }}>
                    {SelectedBarber.name || "Barber"}
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                    {t("YourBarber")}
                  </div>
                </div>
              </div>
              {shouldShowBarberStep && (
                <button
                  onClick={handleBackToBarber}
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    cursor: "pointer",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    outline: "none",
                  }}
                >
                  {t("Change")}
                </button>
              )}
            </div>
          )}

          <div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("SelectYourServices")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              {t("ChooseOneOrMoreServices")}
            </p>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {AllData.barberServices.map((item, index) => {
                const isSelected = SelectedServices.some(
                  (s) => s.Service === item.service
                );
                return (
                  <div
                    key={index}
                    onClick={() => toggleService(item)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "18px",
                      borderRadius: "14px",
                      backgroundColor: isSelected ? "#667eea" : "#fff",
                      color: isSelected ? "#fff" : "#1a1a1a",
                      border:
                        "2px solid " + (isSelected ? "#667eea" : "#e5e7eb"),
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: isSelected
                        ? "0 4px 16px rgba(102, 126, 234, 0.4)"
                        : "0 2px 6px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "4px",
                        }}
                      >
                        {item.service}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          opacity: 0.85,
                        }}
                      >
                        {item.duration || "30 min"} {t("Minutes")}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                      }}
                    >
                      <div style={{ fontWeight: "700", fontSize: "19px" }}>
                        ₪{item.price}
                      </div>
                      <div
                        style={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          border: isSelected
                            ? "2px solid #fff"
                            : "2px solid #d1d5db",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: isSelected ? "#fff" : "transparent",
                        }}
                      >
                        {isSelected && (
                          <div
                            style={{
                              width: "14px",
                              height: "14px",
                              borderRadius: "50%",
                              backgroundColor: "#667eea",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total Price & Duration */}
            {SelectedServices.length > 0 && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "18px",
                  borderRadius: "14px",
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "16px",
                      color: "#374151",
                    }}
                  >
                    {t("TotalDuration")}
                  </span>
                  <span
                    style={{
                      fontWeight: "700",
                      fontSize: "19px",
                      color: "#374151",
                    }}
                  >
                    {totalDuration} {t("Minutes")}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "16px",
                      color: "#374151",
                    }}
                  >
                    {t("TotalPrice")}
                  </span>
                  <span
                    style={{
                      fontWeight: "700",
                      fontSize: "22px",
                      color: "#667eea",
                    }}
                  >
                    ₪{totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Empty State */}
          {SelectedServices.length === 0 && (
            <div
              style={{
                marginTop: "32px",
                padding: "40px 20px",
                textAlign: "center",
                backgroundColor: "#fef9e7",
                borderRadius: "16px",
                border: "2px solid #fde68a",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>✨</div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "6px",
                  fontSize: "16px",
                }}
              >
                {t("SelectAtLeastOneService")}
              </div>
              <div style={{ fontSize: "14px", color: "#b45309" }}>
                {t("ChooseFromServices")}
              </div>
            </div>
          )}

          {/* Next Button - Fixed at bottom */}
          <div
            style={{
              position: "fixed",
              bottom: "70px",
              left: 0,
              right: 0,
              padding: "16px",
              backgroundColor: "#fff",
              borderTop: "1px solid #e5e7eb",
              boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
              zIndex: 100,
            }}
          >
            <button
              onClick={handleNextToDateTime}
              disabled={SelectedServices.length === 0}
              style={{
                width: "100%",
                maxWidth: "600px",
                margin: "0 auto",
                display: "block",
                padding: "18px",
                fontSize: "16px",
                fontWeight: "600",
                border: "none",
                borderRadius: "14px",
                backgroundColor:
                  SelectedServices.length === 0 ? "#d1d5db" : "#667eea",
                color: "#fff",
                cursor:
                  SelectedServices.length === 0 ? "not-allowed" : "pointer",
                boxShadow:
                  SelectedServices.length === 0
                    ? "none"
                    : "0 4px 16px rgba(102, 126, 234, 0.4)",
                transition: "all 0.3s ease",
                outline: "none",
              }}
            >
              {t("NextSelectDateTime")}
            </button>
          </div>
        </div>
      )}

      {/* STEP 6: Select Date & Time */}
      {step === 6 && (
        <div style={{ padding: "0 16px" }}>
          {/* Selected Barber Banner - Show if barber was selected */}
          {SelectedBarber && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 16px",
                background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                borderRadius: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#fff",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    color: "#f59e0b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "16px",
                  }}
                >
                  {SelectedBarber.name?.charAt(0) || "B"}
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "700" }}>
                    {SelectedBarber.name || "Barber"}
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                    {t("YourBarber")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Selected Services Summary */}
          <div
            style={{
              backgroundColor: "#f9fafb",
              borderRadius: "14px",
              padding: "18px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#6b7280",
                  letterSpacing: "0.5px",
                }}
              >
                {t("SelectedServices")}
              </div>
              <button
                onClick={handleBackToServices}
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#667eea",
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  outline: "none",
                }}
              >
                {t("Edit")}
              </button>
            </div>
            {SelectedServices.map((service, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "14px",
                  marginBottom: "8px",
                  color: "#1a1a1a",
                }}
              >
                <div>
                  <span>{service.Service}</span>
                  {service.Duration && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginLeft: "6px",
                      }}
                    >
                      ({service.Duration} min)
                    </span>
                  )}
                </div>
                <span style={{ fontWeight: "600" }}>₪{service.Price}</span>
              </div>
            ))}
            <div
              style={{
                height: "1px",
                backgroundColor: "#e5e7eb",
                margin: "14px 0 10px 0",
              }}
            />

            {/* Total Duration Row */}
            {totalDuration > 0 && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  <span>{t("TotalDuration")}</span>
                  <span>{totalDuration} min</span>
                </div>
                <div
                  style={{
                    height: "1px",
                    backgroundColor: "#e5e7eb",
                    margin: "8px 0",
                  }}
                />
              </>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "17px",
                fontWeight: "700",
                color: "#667eea",
              }}
            >
              <span>Total Price</span>
              <span>₪{totalPrice.toFixed(2)}</span>
            </div>
          </div>

          {/* Select Date */}
          <div style={{ marginTop: "28px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "16px",
              }}
            >
              {t("SelectDate")}
            </h3>
            <div
              style={{
                display: "flex",
                overflowX: "auto",
                gap: "10px",
                padding: "4px 0",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {days.map((item, index) => {
                const isSelected = item.date === ChoosedDate;
                const isToday = item.date === moment().format("DD-MM-YYYY");
                return (
                  <div
                    key={index}
                    onClick={() => {
                      setChoosedDate(item.date);
                      const todaysAppointments = AllApps.filter(
                        (app) => app.date === item.date
                      );
                      setChoosedApps(todaysAppointments);
                    }}
                    style={{
                      minWidth: "76px",
                      padding: "14px 10px",
                      borderRadius: "16px",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#667eea" : "#fff",
                      color: isSelected ? "#fff" : "#1a1a1a",
                      border:
                        isToday && !isSelected
                          ? "2px solid #667eea"
                          : "1px solid #e5e7eb",
                      transition: "all 0.3s ease",
                      boxShadow: isSelected
                        ? "0 4px 16px rgba(102, 126, 234, 0.4)"
                        : "0 2px 6px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "6px",
                        opacity: isSelected ? 1 : 0.7,
                      }}
                    >
                      {item.day}
                    </div>
                    <div style={{ fontSize: "22px", fontWeight: "700" }}>
                      {item.dayNum}
                    </div>
                    {isToday && (
                      <div
                        style={{
                          fontSize: "10px",
                          marginTop: "4px",
                          opacity: 0.9,
                          fontWeight: "600",
                        }}
                      >
                        {t("Today")}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Available Times */}
          <div style={{ marginTop: "28px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "16px",
              }}
            >
              {t("AvailableTimes")}
            </h3>

            {/* Duration info banner */}
            {totalDuration > 0 && (
              <div
                style={{
                  padding: "12px 16px",
                  backgroundColor: "#eff6ff",
                  borderRadius: "12px",
                  marginBottom: "16px",
                  border: "1px solid #bfdbfe",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "20px" }}>⏱️</span>
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#1e40af",
                    }}
                  >
                    {t("TotalDurationNeeded")} {totalDuration} {t("Minutes")}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#3b82f6",
                    }}
                  >
                    {t("ShowingConsecutiveSlots")}
                  </div>
                </div>
              </div>
            )}

            {(() => {
              const validIndexes = getAvailableStartingSlots(
                ChoosedApps,
                totalDuration
              );

              return validIndexes.length === 0 ? (
                <div
                  style={{
                    padding: "40px 20px",
                    textAlign: "center",
                    backgroundColor: "#fef2f2",
                    borderRadius: "16px",
                    border: "2px solid #fca5a5",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                    📅
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: "#991b1b",
                      fontSize: "16px",
                    }}
                  >
                    {t("NoAvailableAppointments")}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#dc2626",
                      marginTop: "6px",
                    }}
                  >
                    {totalDuration > 0
                      ? `No consecutive slots found with ${totalDuration} minutes available. Try selecting another date or fewer services.`
                      : "Please select another date"}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    height: "400px",
                    gap: "12px",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: "10px",
                    paddingRight: "10px",
                  }}
                >
                  {validIndexes.map((index) => {
                    const item = ChoosedApps[index];
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          setObjectData(item);
                          setOpenDrawer(!OpenDrawer);
                        }}
                        style={{
                          width: "100px",
                          height: "calc(25% - 9px)",
                          padding: "18px 14px",
                          borderRadius: "14px",
                          textAlign: "center",
                          backgroundColor: "#fff",
                          border: "1px solid #e5e7eb",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                          flexShrink: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#667eea";
                          e.currentTarget.style.color = "#fff";
                          e.currentTarget.style.transform = "scale(1.05)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(102, 126, 234, 0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff";
                          e.currentTarget.style.color = "#1a1a1a";
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 6px rgba(0,0,0,0.06)";
                        }}
                      >
                        <div style={{ fontSize: "17px", fontWeight: "700" }}>
                          {item.timeStart}
                        </div>
                        {/* <div
                          style={{
                            fontSize: "11px",
                            color: "#6b7280",
                            marginTop: "4px",
                          }}
                        >
                          {item.timeLength} {t("Minutes")}
                        </div> */}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Back Button - Fixed at bottom */}
          <div
            style={{
              position: "fixed",
              bottom: "70px",
              left: 0,
              right: 0,
              padding: "16px",
              backgroundColor: "#fff",
              borderTop: "1px solid #e5e7eb",
              boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
              zIndex: 100,
            }}
          >
            <button
              onClick={handleBackToServices}
              style={{
                width: "100%",
                maxWidth: "600px",
                margin: "0 auto",
                display: "block",
                padding: "18px",
                fontSize: "16px",
                fontWeight: "600",
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                backgroundColor: "#fff",
                color: "#6b7280",
                cursor: "pointer",
                transition: "all 0.3s ease",
                outline: "none",
              }}
            >
              {t("BackToServices")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
